import { Accordion, Group, Stack, Text } from '@mantine/core';
import DatasetIcon from '@/components/DatasetIcon.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import { RunGroup } from '@/pages/AutomationPage/components/ManualRunModal/hooks/runGroupMatching.ts';
import { ManualTriggerInput } from '@/types/automationSteps.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import RunHistoryPopover from '@/pages/AutomationPage/components/ManualRunModal/components/PreviousRunUI/RunHistoryPopover.tsx';

type Props = {
  group: RunGroup;
  isLast: boolean;
  triggers: ManualTriggerInput[];
  getRunUrl: (runId: string) => string;
  onSelectPartialRun?: (run: RunWithDatasets) => void;
};

const PartialRunGroupItem = ({
  group,
  isLast,
  triggers,
  getRunUrl,
  onSelectPartialRun,
}: Props) => {
  const run = group.latest;
  const additionalRunsCount = group.count - 1;
  const summaryText =
    group.missingRequiredTriggers.length > 0
      ? `Partial match: missing ${group.missingRequiredTriggers.length} required input${
          group.missingRequiredTriggers.length === 1 ? '' : 's'
        } from the current trigger setup`
      : group.extraInputCount > 0
        ? `Partial match: includes ${group.extraInputCount} input${
            group.extraInputCount === 1 ? '' : 's'
          } that no longer exist in the trigger setup`
        : `Partial match: ${group.matchedTriggerCount} of ${triggers.length} inputs from the current trigger setup match`;

  return (
    <Accordion.Item
      value={run.run.id}
      key={group.key}
      style={{ border: isLast ? 0 : undefined }}
    >
      <Accordion.Control
        px="md"
        py="xs"
        styles={{
          chevron: {
            marginLeft: '8px',
          },
        }}
      >
        <Group wrap="nowrap" align="center" gap="xs">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="12px" truncate="end" style={{ minWidth: 0, flex: 1 }}>
              {run.run.name}
            </Text>
            <Text size="11px" c="dimmed">
              {summaryText}
            </Text>
          </Stack>

          <Group wrap="nowrap" style={{ flex: '0 0 auto' }} gap={6}>
            <RunHistoryPopover
              group={group}
              getRunUrl={getRunUrl}
              title="Runs with the same matching inputs"
            >
              <Group gap={4} wrap="nowrap" style={{ cursor: 'pointer' }}>
                <Text size="11px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatCalendarStyle(run.run.created_at)}
                </Text>
                {additionalRunsCount > 0 && (
                  <Text size="11px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    +{additionalRunsCount}
                  </Text>
                )}
              </Group>
            </RunHistoryPopover>
          </Group>
        </Group>
      </Accordion.Control>

      <Accordion.Panel styles={{ content: { padding: 0 } }}>
        <Stack px={'xl'} py={'sm'} style={{ background: '#F8F8F8' }} gap="sm">
          <Group justify="space-between" align="center">
            <Stack gap={2}>
              <Text size="12px" fw={500}>
                Matching inputs
              </Text>
              <Text size="11px" c="dimmed">
                Only these inputs will be filled in when you use this run.
              </Text>
            </Stack>
            <StyledButton
              size="sm"
              variant="outline"
              onClick={() => onSelectPartialRun?.(run)}
            >
              Fill matching inputs
            </StyledButton>
          </Group>

          {group.matchedInputs.map((dataset) => (
            <Group
              wrap={'nowrap'}
              key={dataset.id}
              w="100%"
              style={{ minWidth: 0 }}
              gap={'xs'}
            >
              <DatasetIcon dataset={dataset.dataset} size={'16px'} />
              <Text
                size="12px"
                truncate={'end'}
                style={{ minWidth: 0 }}
                title={dataset.dataset.name}
              >
                {dataset.dataset.name}
              </Text>
            </Group>
          ))}

          {group.missingRequiredTriggers.length > 0 && (
            <Stack gap={6}>
              <Text size="10px" fw={500} c="#9E9E9E">
                MISSING REQUIRED INPUTS
              </Text>
              <Group gap={6}>
                {group.missingRequiredTriggers.map((trigger) => (
                  <Text
                    key={trigger.output_id}
                    size="11px"
                    px="8px"
                    py="4px"
                    c="#475569"
                    style={{
                      borderRadius: 999,
                      background: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    {trigger.name}
                  </Text>
                ))}
              </Group>
            </Stack>
          )}

          {group.missingOptionalTriggers.length > 0 && (
            <Stack gap={6}>
              <Text size="10px" fw={500} c="#9E9E9E">
                OPTIONAL INPUTS NOT USED IN THIS RUN
              </Text>
              <Group gap={6}>
                {group.missingOptionalTriggers.map((trigger) => (
                  <Text
                    key={trigger.output_id}
                    size="11px"
                    px="8px"
                    py="4px"
                    c="#475569"
                    style={{
                      borderRadius: 999,
                      background: '#FFFFFF',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    {trigger.name}
                  </Text>
                ))}
              </Group>
            </Stack>
          )}

          {group.extraInputCount > 0 && (
            <Stack gap={4}>
              <Text size="10px" fw={500} c="#9E9E9E">
                NOT IN CURRENT TRIGGER CONFIG
              </Text>
              <Text size="12px" c="dimmed">
                This older run also used {group.extraInputCount} input
                {group.extraInputCount === 1 ? '' : 's'} that no longer exist in the current
                trigger setup.
              </Text>
            </Stack>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default PartialRunGroupItem;
